import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Image, Platform, useWindowDimensions, type TextStyle } from 'react-native';
import { androidScaleSize } from '../../shared/theme/sharedStyles';
import type { MenuBuilder } from '../../data/domains/menu';
import { appTheme } from '../../shared/theme';
import type { ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { DASHBOARD_PADDING_H, DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { resolveImageUri } from '../../shared/utils/resolveImageUri';
import { SvgIcon } from '../../components/common/SvgIcon';
import { HorizontalLtrScrollView } from '../../components/ui/HorizontalLtrScrollView';
import { useRtl } from '../../shared/i18n/useRtl';
import { formatNumber, getApiName, getLocale } from '../../shared/i18n';
import { dashboardNestedTileShadow } from './variants/set1/dashboardSet1Styles';

/** Used by: `DashboardScreen` (selected-meals grid), `OnlineOrderImagesStrip` (reuses `MenuImageTile`). */

/** Card is 1 wide × 1.25 tall; image is 1:1 from the top; caption band is the bottom 25% of the card. */
export const MENU_TILE_CARD_ASPECT = 1.25;
export const MENU_TILE_CAPTION_FRACTION = 0.25;
/** ~3 full tiles + partial 4th in the dashboard strip viewport. */
export const MENU_TILE_STRIP_PEEK_RATIO = 0.34;
export const MENU_TILE_STRIP_GAP = appTheme.spacing.sm;
const STRIP_SHADOW_PADDING = appTheme.spacing.md;
const STRIP_SHADOW_MARGIN = -appTheme.spacing.md;
const TILE_R = 14;
const CAPTION_PAD_H = androidScaleSize(8);
const CAPTION_FONT_SIZE = 10;
const CAPTION_LINE_HEIGHT = 12;
const CAPTION_MAX_LINES = 2;
const ANDROID_CAPTION_TEXT: TextStyle =
  Platform.OS === 'android' ? { includeFontPadding: false, textAlignVertical: 'center' } : {};

export type MenuImageTileMetrics = {
  tile_width: number;
  tile_height: number;
  image_height: number;
  caption_height: number;
};

export function computeMenuImageTileMetrics(
  screen_width: number,
  item_gap: number = MENU_TILE_STRIP_GAP,
): MenuImageTileMetrics {
  const card_pad = appTheme.card.padding.lg;
  const strip_width = screen_width - DASHBOARD_PADDING_H * 2 - card_pad * 2;
  const visible_units = 3 + MENU_TILE_STRIP_PEEK_RATIO;
  const gap_total = item_gap * 3;
  const tile_width = Math.max(72, Math.floor((Math.max(0, strip_width) - gap_total) / visible_units));
  const tile_height = Math.round(tile_width * MENU_TILE_CARD_ASPECT);
  const image_height = tile_width;
  const caption_height = Math.round(tile_height * MENU_TILE_CAPTION_FRACTION);
  return { tile_width, tile_height, image_height, caption_height };
}

function resolveMenuImageCaptionBg(colors: ThemeColors): string {
  return (hexToRgba(colors.surfaceElevated, 1) as string) || colors.surfaceElevated;
}

interface DashboardMenuImagesStripProps {
  menu_builders: MenuBuilder[];
  colors: ThemeColors;
}

export interface FlatItem {
  key: string;
  name: string;
  uri: string | null;
  quantity: number;
}

export function getMenuImagesStripItems(menu_builders: MenuBuilder[]): FlatItem[] {
  const flat: FlatItem[] = [];
  for (const builder of menu_builders) {
    for (const it of builder.type_data ?? []) {
      const q = it.quantity ?? 0;
      if (q > 0) {
        const url = it.attachment ?? (it as any).image;
        const row = it as unknown as Record<string, unknown>;
        flat.push({
          key: `mi-${it.id}-${builder.food_type_id}`,
          name: getApiName(row) || it.name || 'Item',
          uri: resolveImageUri(url),
          quantity: q,
        });
      }
      for (const r of (it as any).replaceable_menu_items ?? []) {
        const rq = r.quantity ?? 0;
        if (rq > 0) {
          const url = r.attachment ?? r.image;
          const row = r as Record<string, unknown>;
          flat.push({
            key: `mi-r-${r.id}-${builder.food_type_id}`,
            name: getApiName(row) || r.name || 'Item',
            uri: resolveImageUri(url),
            quantity: rq,
          });
        }
      }
    }
  }
  return flat.slice(0, 16);
}

/** Collect menu items that have an image (any quantity), shuffle, return up to `count`. */
export function getRandomMenuImageItems(menu_builders: MenuBuilder[], count: number): FlatItem[] {
  const flat: FlatItem[] = [];
  for (const builder of menu_builders) {
    for (const it of builder.type_data ?? []) {
      const url = it.attachment ?? (it as any).image;
      if (url) {
        const row = it as unknown as Record<string, unknown>;
        flat.push({
          key: `mi-${it.id}-${builder.food_type_id}`,
          name: getApiName(row) || it.name || 'Item',
          uri: resolveImageUri(url),
          quantity: it.quantity ?? 0,
        });
      }
      for (const r of (it as any).replaceable_menu_items ?? []) {
        const rurl = r.attachment ?? r.image;
        if (rurl) {
          const row = r as Record<string, unknown>;
          flat.push({
            key: `mi-r-${r.id}-${builder.food_type_id}`,
            name: getApiName(row) || r.name || 'Item',
            uri: resolveImageUri(rurl),
            quantity: r.quantity ?? 0,
          });
        }
      }
    }
  }
  const with_uri = flat.filter((i): i is FlatItem & { uri: string } => Boolean(i.uri));
  for (let i = with_uri.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [with_uri[i], with_uri[j]] = [with_uri[j], with_uri[i]];
  }
  return with_uri.slice(0, count);
}

export const MenuImageTile = React.memo(function MenuImageTile({
  item,
  colors,
  style: style_override,
  tile_metrics,
}: {
  item: FlatItem;
  colors: ThemeColors;
  style?: object;
  tile_metrics?: MenuImageTileMetrics;
}) {
  const [image_load_failed, set_image_load_failed] = useState(false);
  const { textAlign, writingDirection } = useRtl();
  const { width: screen_width } = useWindowDimensions();
  const metrics = tile_metrics ?? computeMenuImageTileMetrics(screen_width);
  const show_remote = Boolean(item.uri) && !image_load_failed;
  const caption_bg = resolveMenuImageCaptionBg(colors);

  useEffect(() => {
    set_image_load_failed(false);
  }, [item.uri]);

  return (
    <View
      style={[
        styles.tile,
        dashboardNestedTileShadow(colors),
        {
          width: metrics.tile_width,
          height: metrics.tile_height,
          borderRadius: TILE_R,
          backgroundColor: colors.surfaceElevated,
        },
        style_override,
      ]}>
      <View
        style={[
          styles.tileShell,
          {
            width: metrics.tile_width,
            height: metrics.tile_height,
            backgroundColor: colors.surfaceElevated,
            borderRadius: TILE_R,
          },
        ]}>
        <View
          style={[
            styles.imageLayer,
            {
              height: metrics.image_height,
              backgroundColor: colors.surfaceElevated,
            },
          ]}>
          {show_remote ? (
            <Image
              source={{ uri: item.uri! }}
              style={styles.image}
              resizeMode="cover"
              fadeDuration={0}
              onError={() => set_image_load_failed(true)}
            />
          ) : (
            <View style={[styles.placeholder, { backgroundColor: colors.primaryUltra || colors.primaryFade }]}>
              <SvgIcon name="meal" size={24} color={colors.primary} />
            </View>
          )}
          {item.quantity > 1 ? (
            <View style={[styles.badge, { backgroundColor: colors.primary }]}>
              <Text style={[styles.badgeText, { color: colors.onPrimary ?? colors.white }]}>
                ×{formatNumber(item.quantity)}
              </Text>
            </View>
          ) : null}
        </View>
        <View
          style={[
            styles.captionBar,
            {
              height: metrics.caption_height,
              backgroundColor: caption_bg,
              borderTopColor: (hexToRgba(colors.text, 0.08) as string) || colors.border,
            },
          ]}
          pointerEvents="none">
          <View style={[styles.captionContent, { paddingHorizontal: CAPTION_PAD_H }]}>
            <Text
              style={[styles.caption, { color: colors.text, textAlign, writingDirection }, ANDROID_CAPTION_TEXT]}
              numberOfLines={CAPTION_MAX_LINES}
              ellipsizeMode="tail">
              {item.name}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
});

export function DashboardMenuImagesStrip({ menu_builders, colors }: DashboardMenuImagesStripProps) {
  const locale = getLocale();
  const items = useMemo(() => getMenuImagesStripItems(menu_builders), [menu_builders, locale]);
  const { width: screen_width } = useWindowDimensions();
  const tile_metrics = useMemo(() => computeMenuImageTileMetrics(screen_width), [screen_width]);

  return (
    <View style={styles.wrap}>
      <Text style={[styles.title, { color: colors.text }]}>Today's picks</Text>
      {items.length === 0 ? (
        <View style={[styles.emptyWrap, { backgroundColor: colors.surfaceElevated }]}>
          <SvgIcon name="meal" size={32} color={colors.primary} />
          <Text style={[styles.emptyText, { color: colors.textSecondary }]}>Select meals from the menu to see them here</Text>
        </View>
      ) : (
        <HorizontalLtrScrollView
          style={[styles.scrollView, { minHeight: tile_metrics.tile_height + 8 }]}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}>
          {items.map(item => (
            <MenuImageTile key={item.key} item={item} colors={colors} tile_metrics={tile_metrics} />
          ))}
        </HorizontalLtrScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    paddingTop: STRIP_SHADOW_PADDING,
    paddingBottom: STRIP_SHADOW_PADDING,
    marginBottom: STRIP_SHADOW_MARGIN * 2,
    overflow: 'visible',
  },
  title: {
    ...appTheme.typography.bodyMedium,
    fontSize: 15,
    fontWeight: '700',
    marginBottom: appTheme.spacing.sm,
    letterSpacing: 0.2,
  },
  emptyWrap: {
    marginHorizontal: 0,
    paddingVertical: appTheme.spacing.xl,
    paddingHorizontal: appTheme.spacing.lg,
    borderRadius: DASH_STUDIO_RADIUS,
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.sm,
  },
  emptyText: {
    ...appTheme.typography.bodySmall,
    fontSize: 13,
    textAlign: 'center',
  },
  loadingWrap: {
    minHeight: 120,
    marginHorizontal: 0,
    marginBottom: STRIP_SHADOW_MARGIN * 2,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: appTheme.spacing.lg,
  },
  scrollView: {},
  scrollContent: {
    paddingTop: STRIP_SHADOW_PADDING,
    paddingBottom: STRIP_SHADOW_PADDING,
    gap: appTheme.spacing.sm,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  tile: {
    position: 'relative',
    overflow: 'visible',
  },
  tileShell: {
    position: 'relative',
    overflow: 'hidden',
  },
  imageLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    overflow: 'hidden',
  },
  image: { ...StyleSheet.absoluteFillObject },
  placeholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  captionBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    elevation: 2,
    overflow: 'hidden',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderBottomLeftRadius: TILE_R,
    borderBottomRightRadius: TILE_R,
  },
  captionContent: {
    height: '100%',
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  caption: {
    width: '100%',
    fontSize: CAPTION_FONT_SIZE,
    fontWeight: '800',
    lineHeight: CAPTION_LINE_HEIGHT,
  },
  badge: {
    position: 'absolute',
    top: 6,
    end: 6,
    zIndex: 3,
    elevation: 3,
    minWidth: 20,
    height: 18,
    borderRadius: 9,
    paddingHorizontal: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    ...appTheme.typography.bodySmall,
    fontSize: 11,
    fontWeight: '600',
  },
});

/** Same chrome as `DashboardMenuImagesStrip` — used by `OnlineOrderImagesStrip` for matching layout. */
export const menuImagesStripStyles = styles;
